import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

/**
 * البند ٢٣: «أين تكلّم الشيخ عن كذا» ← مقطع ورابط تشغيل.
 *
 * البحث النصّي فوريّ ومجّاني فهو الافتراض. و«البحث الذكيّ» نداءُ نموذج له
 * سقف يومي — يُطلب بضغطة لا يجري وحده.
 */
export default function TranscriptSearch() {
  const { api } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const [query, setQuery] = useState('');
  const [smart, setSmart] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async (withSmart) => {
    const q = query.trim();
    if (q.length < 2) return;

    setBusy(true);
    setSmart(withSmart);
    try {
      setResult(await api(`/transcriptions/search?${new URLSearchParams({ q, ...(withSmart ? { smart: 1 } : {}) })}`));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const openAt = (hit) => {
    const at = hit.start_seconds == null ? '' : `&t=${Math.floor(hit.start_seconds)}`;
    navigate(`/transcription?id=${hit.transcription.id}${at}`);
  };

  const Hit = ({ hit, highlight }) => (
    <div
      className="card"
      style={{ padding: 14, cursor: 'pointer', borderRight: highlight ? '4px solid var(--c-blue-500)' : undefined }}
      onClick={() => openAt(hit)}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
        <span style={{ fontWeight: 600, fontSize: 14 }}>{hit.transcription.title}</span>
        <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {hit.clock && (
            <span style={{
              fontFamily: 'monospace', fontSize: 12, color: 'var(--c-blue-500)',
              background: 'var(--c-blue-100)', padding: '2px 6px', borderRadius: 4,
            }}>{hit.clock}</span>
          )}
          {hit.link && (
            <a href={hit.link} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ fontSize: 12 }}>
              تشغيل ↗
            </a>
          )}
        </span>
      </div>

      {hit.quote ? (
        <>
          <div dir="rtl" style={{ fontSize: 14, lineHeight: 1.9, fontWeight: 500 }}>«{hit.quote}»</div>
          {hit.why && (
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{hit.why}</div>
          )}
          {!hit.quote_timed && (
            <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 4 }}>
              ⚠️ لم يُطابَق الاقتباس في الكلمات الموقّتة — الوقت وقت المقطع لا وقت العبارة
            </div>
          )}
        </>
      ) : (
        <div dir="rtl" style={{ fontSize: 13, lineHeight: 1.9, color: 'var(--text-muted)' }}>{hit.snippet}</div>
      )}
    </div>
  );

  return (
    <div dir="rtl" style={{ maxWidth: 900, margin: '0 auto' }}>
      <div className="card" style={{ padding: 20, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
          <span style={{ fontSize: 24 }}>🔍</span>
          <h3 style={{ margin: 0, fontSize: 17 }}>البحث في كل التفريغات</h3>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16, lineHeight: 1.7 }}>
          «أين تكلّم الشيخ عن كذا» — يُعاد الموضع نفسه في الدرس مع رابط يفتحه عند ثانيته،
          لا الملفّ كلّه.
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input
            className="form-input"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !busy && run(false)}
            disabled={busy}
            placeholder="نواقض الوضوء، حكم صيام المسافر، شروط الوقف..."
            style={{ flex: '1 1 280px' }}
          />
          <button className="btn btn-primary" onClick={() => run(false)} disabled={busy || query.trim().length < 2}>
            {busy && !smart ? '⏳' : '🔍 بحث'}
          </button>
          <button
            className="btn btn-outline"
            onClick={() => run(true)}
            disabled={busy || query.trim().length < 2}
            title="يقرأ النموذج المقاطع ويستخرج العبارة المجيبة — له سقف يومي"
          >
            {busy && smart ? '⏳ جارٍ' : '✨ بحث ذكيّ'}
          </button>
        </div>
      </div>

      {result && (
        <>
          {result.smart?.error && (
            <div className="card" style={{ background: 'var(--c-coral-soft)', fontSize: 13, marginBottom: 12 }}>
              ⚠️ {result.smart.error}
            </div>
          )}

          {result.smart?.answers?.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                ✨ المواضع المجيبة
                {result.smart.cached && (
                  <span style={{ fontWeight: 400, color: 'var(--text-faint)' }}> — نتيجة محفوظة لهذا السؤال</span>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {result.smart.answers.map(hit => <Hit key={`s-${hit.passage_id}`} hit={hit} highlight />)}
              </div>
            </div>
          )}

          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
            نتائج نصّية ({result.total})
          </div>
          {result.results.length === 0 ? (
            <div className="card" style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              لا مواضع تجمع كلمات البحث كلّها — جرّب كلمات أقلّ أو أعمّ.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {result.results.map(hit => <Hit key={hit.passage_id} hit={hit} />)}
            </div>
          )}
        </>
      )}
    </div>
  );
}
